import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
  Badge,
  Button,
  IconButton,
  HStack,
  VStack,
  Flex,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  useDisclosure,
  Switch,
  useToast,
  InputGroup,
  InputLeftElement,
  Card,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  SimpleGrid,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Checkbox,
  CheckboxGroup,
  Stack,
  Divider
} from '@chakra-ui/react';
import {
  MdSearch,
  MdRefresh,
  MdAdd,
  MdMoreVert,
  MdEdit,
  MdDelete,
  MdLink
} from 'react-icons/md';
import apiService from '../services/apiService';
import { useNavigate } from 'react-router-dom';

const ApplicationsManagement = ({ userProfile, appConfig, authServiceReady }) => {
  // ALL THEME-SPECIFIC COLORS - MUST BE AT THE TOP BEFORE ANY CONDITIONAL LOGIC
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const headingColor = useColorModeValue('gray.800', 'white');
  const mutedTextColor = useColorModeValue('gray.500', 'gray.400');
  const inputBg = useColorModeValue('white', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.900');
  const statsCardBg = useColorModeValue('white', 'gray.800');
  const buttonHoverBorderColor = useColorModeValue('gray.300', 'gray.600');
  const purpleHoverBg = useColorModeValue('purple.50', 'purple.900');
  const purpleBorderColor = useColorModeValue('purple.200', 'purple.600');
  const purpleTextColor = useColorModeValue('purple.600', 'purple.300');
  const purpleHoverBorderColor = useColorModeValue('purple.300', 'purple.500');
  const blueHoverColor = useColorModeValue('blue.600', 'blue.500');
  const badgeVariant = useColorModeValue('subtle', 'solid');
  const modalOverlayBg = useColorModeValue('blackAlpha.300', 'blackAlpha.700');
  const menuShadow = useColorModeValue('lg', 'dark-lg');
  const redHoverBg = useColorModeValue('red.50', 'red.900');
  const paginationHoverBorderColor = useColorModeValue('gray.300', 'gray.600');
  const searchIconColor = useColorModeValue('gray.400', 'gray.500');
  const pageSizeHoverBorderColor = useColorModeValue('gray.300', 'gray.600');

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  // State management
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [pageSize, setPageSize] = useState('20 per page');
  const [currentPage, setCurrentPage] = useState(1);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingApplication, setEditingApplication] = useState(null);
  const [newApplication, setNewApplication] = useState({
    name: '',
    type: '',
    description: '',
    status: true,
    latestversion: '',
    minimalSupportedVersion: ''
  });

  // Application Mapping State
  const [clients, setClients] = useState([]);
  const [availableApplications, setAvailableApplications] = useState([]);
  const [mappingData, setMappingData] = useState({
    selectedClientId: '',
    selectedApplications: []
  });
  const [mappingLoading, setMappingLoading] = useState(false);

  const [versionErrors, setVersionErrors] = useState({});
  const navigate = useNavigate();

  // Fetch applications data
  const fetchApplications = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.fetchClientApplications();

      if (response.success && response.data) {
        setApplications(Array.isArray(response.data) ? response.data : []);
      } else {
        throw new Error(response.error || response.message || 'Failed to fetch applications');
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
      setError(error.message || 'Failed to load applications. Please try again.');
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch clients for mapping
  const fetchClients = async () => {
    try {
      setMappingLoading(true);
      const response = await apiService.getClientsList({ size: 1000 });

      if (response.success && response.data && response.data.content) {
        const clientsList = Array.isArray(response.data.content) ? response.data.content : [];
        const activeClients = clientsList.filter(client => !client.deleted);
        setClients(activeClients);
      } else {
        throw new Error(response.error || response.message || 'Failed to fetch clients');
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast({
        title: 'Error',
        description: 'Failed to load clients. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setClients([]);
    } finally {
      setMappingLoading(false);
    }
  };

  // Fetch available applications for mapping to selected client
  const fetchAvailableApplicationsForClient = async (clientId) => {
    try {
      setMappingLoading(true);
      const response = await apiService.fetchAvailableApplicationsForClient(clientId);

      if (response.success && response.data) {
        setAvailableApplications(Array.isArray(response.data) ? response.data : []);
      } else {
        throw new Error(response.error || response.message || 'Failed to fetch available applications');
      }
    } catch (error) {
      console.error('Error fetching available applications:', error);
      toast({
        title: 'Error',
        description: 'Failed to load available applications. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setAvailableApplications([]);
    } finally {
      setMappingLoading(false);
    }
  };

  // Load applications on component mount
  useEffect(() => {
    fetchApplications();
    fetchClients();
  }, []);

  // Filter out deleted applications
  const activeApplications = applications.filter(app => app.isDelete === 0);

  const stats = {
    totalApplications: activeApplications.length,
    activeApplications: activeApplications.filter(app => app.appStatus === true).length,
    inactiveApplications: activeApplications.filter(app => app.appStatus === false).length
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setStatusFilter('All Status');
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    fetchApplications();
  };

  const handleCreateNew = () => {
    setIsEditMode(false);
    setEditingApplication(null);
    setNewApplication({ name: '', type: '', description: '', status: true, latestversion: '', minimalSupportedVersion: '' });
    onOpen();
  };


  const handleModalClose = () => {
    setIsEditMode(false);
    setEditingApplication(null);
    setNewApplication({ name: '', type: '', description: '', status: true, latestversion: '', minimalSupportedVersion: '' });
    setVersionErrors({})
    onClose();
  };


  const handleCreateApplication = async () => {

    const versionValidationErrors = validateVersions(
      newApplication.latestversion,
      newApplication.minimalSupportedVersion
    );
    setVersionErrors(versionValidationErrors);
    if (!newApplication.name || !newApplication.type) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (Object.keys(versionValidationErrors).length > 0) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the version validation errors before proceeding.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return; 
    }
    try {
      if (isEditMode) {
        await handleUpdateApplication();
      } else {
        await handleCreateNewApplication();
      }
    } catch (error) {
      console.error('Error in handleCreateApplication:', error);

      toast({
        title: 'Error',
        description: error.message || `Failed to ${isEditMode ? 'update' : 'create'} application. Please try again.`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCreateNewApplication = async () => {
    const applicationData = {
      name: newApplication.name,
      type: newApplication.type,
      description: newApplication.description,
      status: newApplication.status,
      version: newApplication.latestversion,
      minimalVersion: newApplication.minimalSupportedVersion
    };

    const response = await apiService.createClientApplication(applicationData);

    if (response.success && response.data) {
      const apiData = response.data;

      const newApp = {
        appId: apiData.appId,
        appName: apiData.appName,
        appStatus: apiData.appStatus,
        appType: apiData.appType,
        appDescription: apiData.appDescription,
        isDelete: apiData.isDelete,
        appVersion: apiData.appVersion
      };

      setApplications(prevApplications => [...prevApplications, newApp]);
      handleModalClose();

      toast({
        title: 'Application Created',
        description: response.message || `${newApp.appName} has been successfully created.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } else {
      throw new Error(response.error || response.message || 'Failed to create application');
    }
  };

  const handleUpdateApplication = async () => {
    const applicationData = {
      id: editingApplication.appId,
      name: newApplication.name,
      type: newApplication.type,
      description: newApplication.description,
      status: newApplication.status,
      version: newApplication.latestversion,
      minimalVersion:newApplication.minimalSupportedVersion
    };

    const response = await apiService.updateClientApplication(applicationData);

    if (response.success && response.data) {
      const apiData = response.data;

      const updatedApp = {
        appId: apiData.appId || editingApplication.appId,
        appName: apiData.appName || newApplication.name,
        appStatus: apiData.appStatus !== undefined ? apiData.appStatus : newApplication.status,
        appType: apiData.appType || newApplication.type,
        appDescription: apiData.appDescription || newApplication.description,
        isDelete: apiData.isDelete !== undefined ? apiData.isDelete : editingApplication.isDelete,
        appVersion: apiData.appVersion || newApplication.version,
        minimalVersion:apiData.minimalVersion||newApplication.minimalVersion

      };

      setApplications(prevApplications =>
        prevApplications.map(app =>
          app.appId === editingApplication.appId ? updatedApp : app
        )
      );

      handleModalClose();

      toast({
        title: 'Application Updated',
        description: response.message || `${updatedApp.appName} has been successfully updated.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } else {
      throw new Error(response.error || response.message || 'Failed to update application');
    }
  };

  const handleEdit = async (application) => {
    try {
      setIsEditMode(true);
      setEditingApplication(application);
      setNewApplication({
        name: application.appName,
        type: application.appType,
        description: application.appDescription,
        status: application.appStatus,
        latestversion: application.appVersion,
        minimalSupportedVersion: application.minimalVersion || ''

      });

      onOpen();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load application details. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDelete = async (application) => {
    try {
      console.log(application);
      // const response=await apiService.deleteClientApplicationMapping();
console.log(userProfile)
       const reqData={
        appId: application.appId,
        updatedBy: userProfile.email
      }
      const response=await apiService.deleteClientApplication(reqData);
      if(response.success && response.data){
        setApplications(apps =>
          apps.map(app =>
            app.appId === application.appId
              ? { ...app, isDelete: 1 }
              : app
          )
        );
  
        toast({
          title: 'Application Deleted',
          description: `${application.appName} has been deleted.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }else{
        toast({
          title: 'Error',
          description: 'Failed to delete application. Please try again. else',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.log("error is ", error)
      toast({
        title: 'Error',
        description: 'Failed to delete application. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };


  const filteredApplications = activeApplications.filter(app => {
    const matchesSearch = searchTerm === '' ||
      app.appName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.appDescription.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.appType.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'All Status' ||
      (statusFilter === 'active' && app.appStatus === true) ||
      (statusFilter === 'inactive' && app.appStatus === false);

    return matchesSearch && matchesStatus;
  });

  // Pagination calculations
  const itemsPerPage = parseInt(pageSize.split(' ')[0]);
  const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedApplications = filteredApplications.slice(startIndex, endIndex);

  const getTypeBadge = (appType) => {
    const typeColorMap = {
      'Web Application': 'blue',
      'WAR': 'orange',
      'Console Application': 'green',
      'Plugins': 'purple',
      'API Service': 'teal',
      'ETL': 'cyan',
      'Others': 'gray'
    };

    return typeColorMap[appType] || 'gray';
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const getStatusBadge = (status) => {
    return status
      ? { colorScheme: 'green', label: 'ACTIVE' }
      : { colorScheme: 'red', label: 'INACTIVE' };
  };

  const getTypeOptions = [
    'Web Application',
    'WAR',
    'Console Application',
    'Plugins',
    'API Service',
    'ETL',
    'Others'
  ];


  // const validateVersions = (latestVersion, minimalVersion) => {
  //   const errors = {};

  //   // Version format validation regex - allows X.Y or X.Y.Z format
  //   const versionPattern = /^\d+\.\d+(\.\d+)?$/;

  //   // Validate latest version
  //   if (!latestVersion?.trim()) {
  //     errors.latestversion = 'Latest version is required';
  //   } else if (!versionPattern.test(latestVersion.trim())) {
  //     errors.latestversion = 'Latest version must be in format X.Y or X.Y.Z (e.g., 6.2, 5.3.1)';
  //   }

  //   // Validate minimal supported version
  //   if (!minimalVersion?.trim()) {
  //     errors.minimalSupportedVersion = 'Minimum supported version is required';
  //   } else if (!versionPattern.test(minimalVersion.trim())) {
  //     errors.minimalSupportedVersion = 'Minimum supported version must be in format X.Y or X.Y.Z (e.g., 6.2, 5.3.1)';
  //   }

  //   // Compare versions if both are valid format
  //   if (!errors.latestversion && !errors.minimalSupportedVersion && latestVersion?.trim() && minimalVersion?.trim()) {
  //     const compareVersions = (v1, v2) => {
  //       const v1Parts = v1.split('.').map(Number);
  //       const v2Parts = v2.split('.').map(Number);

  //       // Normalize arrays to same length
  //       const maxLength = Math.max(v1Parts.length, v2Parts.length);
  //       while (v1Parts.length < maxLength) v1Parts.push(0);
  //       while (v2Parts.length < maxLength) v2Parts.push(0);

  //       for (let i = 0; i < maxLength; i++) {
  //         if (v1Parts[i] < v2Parts[i]) return -1;
  //         if (v1Parts[i] > v2Parts[i]) return 1;
  //       }
  //       return 0;
  //     };

  //     if (compareVersions(minimalVersion.trim(), latestVersion.trim()) > 0) {
  //       errors.minimalSupportedVersion = 'Minimum supported version must be less than or equal to latest version';
  //     }
  //   }

  //   return errors;
  // };

  const validateVersions = (latestVersion, minimalVersion) => {
    const errors = {};
  
    // Version format validation regex - allows X.Y, X.Y.Z, or X.Y.Z.W (max 4 parts)
    const versionPattern = /^\d+\.\d+(\.\d+){0,2}$/;
  
    // Validate latest version
    if (!latestVersion?.trim()) {
      errors.latestversion = 'Latest version is required';
    } else if (!versionPattern.test(latestVersion.trim())) {
      errors.latestversion = 'Latest version must be in format X.Y, X.Y.Z, or X.Y.Z.W (e.g., 6.2, 5.3.1, 5.2.3.1)';
    }
  
    // Validate minimal supported version
    if (!minimalVersion?.trim()) {
      errors.minimalSupportedVersion = 'Minimum supported version is required';
    } else if (!versionPattern.test(minimalVersion.trim())) {
      errors.minimalSupportedVersion = 'Minimum supported version must be in format X.Y, X.Y.Z, or X.Y.Z.W (e.g., 6.2, 5.3.1, 5.2.3.1)';
    }
  
    // Compare versions if both are valid format
    if (!errors.latestversion && !errors.minimalSupportedVersion && latestVersion?.trim() && minimalVersion?.trim()) {
      const compareVersions = (v1, v2) => {
        const v1Parts = v1.split('.').map(Number);
        const v2Parts = v2.split('.').map(Number);
  
        // Normalize arrays to same length
        const maxLength = Math.max(v1Parts.length, v2Parts.length);
        while (v1Parts.length < maxLength) v1Parts.push(0);
        while (v2Parts.length < maxLength) v2Parts.push(0);
  
        for (let i = 0; i < maxLength; i++) {
          if (v1Parts[i] < v2Parts[i]) return -1;
          if (v1Parts[i] > v2Parts[i]) return 1;
        }
        return 0;
      };
  
      if (compareVersions(minimalVersion.trim(), latestVersion.trim()) > 0) {
        errors.minimalSupportedVersion = 'Minimum supported version must be less than or equal to latest version';
      }
    }
  
    return errors;
  };
  const handleViewClientMappings = useCallback((applicationName) => {
    try {
      const mappingsPath = `/client-appmappings?filterByApplication=${encodeURIComponent(applicationName)}`;
      navigate(mappingsPath);
    } catch (error) {
      console.error('Failed to navigate to client mappings:', error);
      // Add toast error handling if you have toast
    }
  }, [navigate]);
  // Loading state
  if (loading) {
    return (
      <Box>
        <Center py={20}>
          <VStack spacing={4}>
            <Spinner size="xl" color="blue.500" thickness="4px" />
            <Text color={textColor} fontSize="lg">Loading applications...</Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box>
        <Alert status="error" bg={cardBg} borderColor="red.300" borderWidth="1px" borderRadius="md">
          <AlertIcon />
          <Box flex="1">
            <AlertTitle color="red.600">Error Loading Applications!</AlertTitle>
            <AlertDescription color={textColor}>
              {error}
            </AlertDescription>
          </Box>
          <Button size="sm" colorScheme="red" variant="outline" onClick={handleRefresh}>
            Retry
          </Button>
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Flex justify="space-between" align="flex-start" mb={6}>
        <Box>
          <Text fontSize="xl" fontWeight="bold" color={headingColor} mb={1}>
            Applications
          </Text>
          <Text color={textColor} fontSize="sm">
            Manage and configure applications
          </Text>
        </Box>
        <HStack spacing={3}>
          <Button
            leftIcon={<MdRefresh />}
            variant="outline"
            size="sm"
            colorScheme="gray"
            borderColor={borderColor}
            color={textColor}
            onClick={handleRefresh}
            _hover={{
              bg: hoverBg,
              borderColor: buttonHoverBorderColor
            }}
          >
            Refresh
          </Button>
          <Button
            leftIcon={<MdAdd />}
            colorScheme="blue"
            size="sm"
            onClick={handleCreateNew}
            _hover={{
              bg: blueHoverColor
            }}
          >
            Create Application
          </Button>
        </HStack>
      </Flex>

      {/* Stats Cards */}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={6}>
        <Card bg={statsCardBg} boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
          <CardBody>
            <Stat>
              <StatLabel color={textColor} fontSize="sm" fontWeight="medium">Total Applications</StatLabel>
              <StatNumber fontSize="2xl" fontWeight="bold" color={headingColor}>
                {stats.totalApplications}
              </StatNumber>
            </Stat>
          </CardBody>
        </Card>
        <Card bg={statsCardBg} boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
          <CardBody>
            <Stat>
              <StatLabel color={textColor} fontSize="sm" fontWeight="medium">Active Applications</StatLabel>
              <StatNumber fontSize="2xl" fontWeight="bold" color="green.500">
                {stats.activeApplications}
              </StatNumber>
            </Stat>
          </CardBody>
        </Card>
        <Card bg={statsCardBg} boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
          <CardBody>
            <Stat>
              <StatLabel color={textColor} fontSize="sm" fontWeight="medium">Inactive Applications</StatLabel>
              <StatNumber fontSize="2xl" fontWeight="bold" color="red.500">
                {stats.inactiveApplications}
              </StatNumber>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Search and Filters */}
      <Card bg={cardBg} boxShadow="sm" mb={6} borderWidth="1px" borderColor={borderColor}>
        <CardBody>
          <Flex gap={4} mb={4} align="end">
            <Box flex="1">
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <MdSearch color={searchIconColor} />
                </InputLeftElement>
                <Input
                  placeholder="Search by application name, description, or type"
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  bg={inputBg}
                  borderColor={borderColor}
                  color={headingColor}
                  _placeholder={{ color: mutedTextColor }}
                  _hover={{ borderColor: buttonHoverBorderColor }}
                  _focus={{
                    borderColor: 'blue.500',
                    boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                  }}
                />
              </InputGroup>
            </Box>
            <Button
              variant="outline"
              colorScheme="purple"
              size="md"
              onClick={resetFilters}
              borderColor={purpleBorderColor}
              color={purpleTextColor}
              _hover={{
                bg: purpleHoverBg,
                borderColor: purpleHoverBorderColor
              }}
            >
              Reset Filters
            </Button>
          </Flex>

          <Text fontSize="sm" fontWeight="medium" color={headingColor} mb={3}>
            Filters
          </Text>

          <HStack spacing={4} mb={4}>
            <FormControl maxW="300px">
              <FormLabel fontSize="sm" color={textColor} fontWeight="medium">Status</FormLabel>
              <Select
                size="sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                bg={inputBg}
                borderColor={borderColor}
                color={headingColor}
                _hover={{ borderColor: buttonHoverBorderColor }}
                _focus={{
                  borderColor: 'blue.500',
                  boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                }}
              >
                <option value="All Status">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Select>
            </FormControl>

            <FormControl maxW="200px">
              <FormLabel fontSize="sm" color={textColor} fontWeight="medium">Page Size</FormLabel>
              <Select
                size="sm"
                value={pageSize}
                onChange={(e) => handlePageSizeChange(e.target.value)}
                bg={inputBg}
                borderColor={borderColor}
                color={headingColor}
                _hover={{ borderColor: pageSizeHoverBorderColor }}
                _focus={{
                  borderColor: 'blue.500',
                  boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                }}
              >
                <option value="20 per page">20 per page</option>
                <option value="50 per page">50 per page</option>
                <option value="100 per page">100 per page</option>
              </Select>
            </FormControl>
          </HStack>
        </CardBody>
      </Card>

      {/* Applications Table */}
      <Card bg={cardBg} boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
        <CardBody p={0}>
          <Flex justify="space-between" align="center" p={4} borderBottom="1px" borderBottomColor={borderColor}>
            <Text fontWeight="semibold" fontSize="md" color={headingColor}>
              Applications ({filteredApplications.length})
            </Text>
            <HStack spacing={4} fontSize="sm" color={textColor}>
              <Text>PAGE {currentPage} OF {totalPages || 1}</Text>
              <Text>SHOWING {Math.min(itemsPerPage, filteredApplications.length - startIndex)} OF {filteredApplications.length}</Text>
            </HStack>
          </Flex>

          {filteredApplications.length === 0 ? (
            <Center py={10}>
              <VStack spacing={3}>
                <Text color={mutedTextColor} fontSize="lg">No applications found</Text>
                <Text color={mutedTextColor} fontSize="sm">
                  {searchTerm || statusFilter !== 'All Status'
                    ? 'Try adjusting your filters'
                    : 'Create your first application to get started'
                  }
                </Text>
                {(!searchTerm && statusFilter === 'All Status') && (
                  <Button colorScheme="blue" size="sm" onClick={handleCreateNew}>
                    Create Application
                  </Button>
                )}
              </VStack>
            </Center>
          ) : (
            <>
              <TableContainer>
                <Table variant="simple" size="sm">
                  <Thead bg={tableHeaderBg} borderBottom="2px" borderColor={borderColor}>
                    <Tr>
                      <Th
                        color={headingColor}
                        fontWeight="semibold"
                        fontSize="xs"
                        textTransform="uppercase"
                        letterSpacing="wider"
                        py={4}
                      >
                        Application
                      </Th>
                      <Th
                        color={headingColor}
                        fontWeight="semibold"
                        fontSize="xs"
                        textTransform="uppercase"
                        letterSpacing="wider"
                        py={4}
                      >
                        Version
                      </Th>
                      <Th
                        color={headingColor}
                        fontWeight="semibold"
                        fontSize="xs"
                        textTransform="uppercase"
                        letterSpacing="wider"
                        py={4}
                      >
                        Type
                      </Th>
                      <Th
                        color={headingColor}
                        fontWeight="semibold"
                        fontSize="xs"
                        textTransform="uppercase"
                        letterSpacing="wider"
                        py={4}
                      >
                        Description
                      </Th>
                      <Th
                        color={headingColor}
                        fontWeight="semibold"
                        fontSize="xs"
                        textTransform="uppercase"
                        letterSpacing="wider"
                        py={4}
                      >
                        Status
                      </Th>
                      <Th
                        color={headingColor}
                        fontWeight="semibold"
                        fontSize="xs"
                        textTransform="uppercase"
                        letterSpacing="wider"
                        w="140px"
                        py={4}
                      >
                        Actions
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {paginatedApplications.map((application, index) => {
                      const statusBadge = getStatusBadge(application.appStatus);

                      return (
                        <Tr key={`${application.appId}-${index}`} _hover={{ bg: hoverBg }}>
                          <Td py={4} borderColor={borderColor}>
                            <VStack align="start" spacing={1}>
                              <Text
                                fontWeight="medium"
                                fontSize="sm"
                                color="blue.500"
                                cursor="pointer"
                                _hover={{ textDecoration: 'underline', color: 'blue.600' }}
                                onClick={() => handleViewClientMappings(application.appName)}
                              >
                                {application.appName}
                              </Text>
                              <Text fontSize="xs" color={mutedTextColor}>
                                ID: {application.appId}
                              </Text>
                            </VStack>
                          </Td>
                          <Td py={4} borderColor={borderColor}>
                            <VStack align="start" spacing={1}>
                              <Text fontWeight="medium" fontSize="sm" color={headingColor}>
                                V{application.appVersion}
                              </Text>
                            </VStack>
                          </Td>
                          <Td py={4} borderColor={borderColor}>
                            <Badge
                              colorScheme={getTypeBadge(application.appType)}
                              variant={badgeVariant}
                              fontSize="xs"
                              px={2}
                              py={1}
                            >
                              {application.appType}
                            </Badge>
                          </Td>
                          <Td py={4} borderColor={borderColor}>
                            <Text fontSize="sm" color={textColor} maxW="300px" noOfLines={2}>
                              {application.appDescription}
                            </Text>
                          </Td>
                          <Td py={4} borderColor={borderColor}>
                            <Badge
                              colorScheme={statusBadge.colorScheme}
                              variant={badgeVariant}
                              fontSize="xs"
                              px={2}
                              py={1}
                            >
                              {statusBadge.label}
                            </Badge>
                          </Td>
                          <Td py={4} borderColor={borderColor}>
                            <Menu>
                              <MenuButton
                                as={IconButton}
                                icon={<MdMoreVert />}
                                variant="ghost"
                                size="sm"
                                color={textColor}
                                _hover={{
                                  bg: hoverBg,
                                  color: headingColor
                                }}
                              />
                              <MenuList
                                bg={cardBg}
                                borderColor={borderColor}
                                boxShadow={menuShadow}
                              >
                                <MenuItem
                                  icon={<MdEdit />}
                                  onClick={() => handleEdit(application)}
                                  bg={cardBg}
                                  color={headingColor}
                                  _hover={{ bg: hoverBg }}
                                >
                                  Edit
                                </MenuItem>
                                <MenuItem
                                  icon={<MdDelete />}
                                  onClick={() => handleDelete(application)}
                                  color="red.500"
                                  bg={cardBg}
                                  _hover={{ bg: redHoverBg }}
                                >
                                  Delete
                                </MenuItem>
                              </MenuList>
                            </Menu>
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </TableContainer>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <Flex justify="space-between" align="center" p={4} borderTop="1px" borderTopColor={borderColor}>
                  <Text fontSize="sm" color={textColor}>
                    Showing {startIndex + 1} to {Math.min(endIndex, filteredApplications.length)} of {filteredApplications.length} entries
                  </Text>

                  <HStack spacing={2}>
                    <Button
                      size="sm"
                      onClick={() => handlePageChange(1)}
                      isDisabled={currentPage === 1}
                      variant="outline"
                      borderColor={borderColor}
                      color={textColor}
                      _hover={{ bg: hoverBg }}
                      _disabled={{
                        opacity: 0.4,
                        cursor: 'not-allowed'
                      }}
                    >
                      First
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      isDisabled={currentPage === 1}
                      variant="outline"
                      borderColor={borderColor}
                      color={textColor}
                      _hover={{ bg: hoverBg }}
                      _disabled={{
                        opacity: 0.4,
                        cursor: 'not-allowed'
                      }}
                    >
                      Previous
                    </Button>

                    {/* Page Numbers */}
                    {(() => {
                      const pages = [];
                      const maxVisible = 5;
                      let startPage = Math.max(1, currentPage - 2);
                      let endPage = Math.min(totalPages, startPage + maxVisible - 1);

                      if (endPage - startPage < maxVisible - 1) {
                        startPage = Math.max(1, endPage - maxVisible + 1);
                      }

                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <Button
                            key={i}
                            size="sm"
                            variant={i === currentPage ? "solid" : "outline"}
                            colorScheme={i === currentPage ? "blue" : "gray"}
                            onClick={() => handlePageChange(i)}
                            borderColor={i === currentPage ? 'blue.500' : borderColor}
                            color={i === currentPage ? 'white' : textColor}
                            bg={i === currentPage ? 'blue.500' : 'transparent'}
                            _hover={{
                              bg: i === currentPage ? 'blue.600' : hoverBg,
                              borderColor: i === currentPage ? 'blue.600' : paginationHoverBorderColor
                            }}
                          >
                            {i}
                          </Button>
                        );
                      }
                      return pages;
                    })()}

                    <Button
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      isDisabled={currentPage === totalPages}
                      variant="outline"
                      borderColor={borderColor}
                      color={textColor}
                      _hover={{ bg: hoverBg }}
                      _disabled={{
                        opacity: 0.4,
                        cursor: 'not-allowed'
                      }}
                    >
                      Next
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handlePageChange(totalPages)}
                      isDisabled={currentPage === totalPages}
                      variant="outline"
                      borderColor={borderColor}
                      color={textColor}
                      _hover={{ bg: hoverBg }}
                      _disabled={{
                        opacity: 0.4,
                        cursor: 'not-allowed'
                      }}
                    >
                      Last
                    </Button>
                  </HStack>
                </Flex>
              )}
            </>
          )}
        </CardBody>
      </Card>

      {/* Create/Edit Application Modal */}
      <Modal isOpen={isOpen} onClose={handleModalClose} size="lg">
        <ModalOverlay bg={modalOverlayBg} />
        <ModalContent bg={cardBg} borderColor={borderColor} borderWidth="1px">
          <ModalHeader color={headingColor} borderBottom="1px" borderBottomColor={borderColor}>
            {isEditMode ? 'Edit Application' : 'Create New Application'}
          </ModalHeader>
          <ModalCloseButton color={textColor} />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel color={headingColor} fontSize="sm" fontWeight="medium">Application Name</FormLabel>
                <Input
                  placeholder="Enter application name"
                  value={newApplication.name}
                  onChange={(e) => setNewApplication({ ...newApplication, name: e.target.value })}
                  bg={inputBg}
                  borderColor={borderColor}
                  color={headingColor}
                  _placeholder={{ color: mutedTextColor }}
                  _hover={{ borderColor: buttonHoverBorderColor }}
                  _focus={{
                    borderColor: 'blue.500',
                    boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                  }}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel color={headingColor} fontSize="sm" fontWeight="medium">Application Type</FormLabel>
                <Select
                  placeholder="Select application type"
                  value={newApplication.type}
                  onChange={(e) => setNewApplication({ ...newApplication, type: e.target.value })}
                  bg={inputBg}
                  borderColor={borderColor}
                  color={headingColor}
                  _hover={{ borderColor: buttonHoverBorderColor }}
                  _focus={{
                    borderColor: 'blue.500',
                    boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                  }}
                >
                  {getTypeOptions.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel color={headingColor} fontSize="sm" fontWeight="medium">Status</FormLabel>
                <HStack spacing={3}>
                  <Switch
                    id="status-toggle"
                    size="md"
                    colorScheme="green"
                    isChecked={newApplication.status}
                    onChange={(e) => setNewApplication({ ...newApplication, status: e.target.checked })}
                  />
                  <Text fontSize="sm" color={headingColor} fontWeight="medium">
                    {newApplication.status ? 'Active' : 'Inactive'}
                  </Text>
                </HStack>
                <Text fontSize="xs" color={mutedTextColor} mt={1}>
                  Toggle to set the application status
                </Text>
              </FormControl>

              <FormControl isRequired isInvalid={!!versionErrors.latestversion}>
                <FormLabel color={headingColor} fontSize="sm" fontWeight="medium" isInvalid={!!versionErrors.latestversion}>Latest Version</FormLabel>
                <Input
                  placeholder="Enter Latest application Version like 1.0 / 2.0"
                  value={newApplication.latestversion}
                  onChange={(e) => {
                    setNewApplication({ ...newApplication, latestversion: e.target.value });
                    // Clear errors when user starts typing
                    if (versionErrors.latestversion) {
                      setVersionErrors(prev => ({ ...prev, latestversion: '' }));
                    }
                  }}
                  bg={inputBg}
                  borderColor={borderColor}
                  color={headingColor}
                  _placeholder={{ color: mutedTextColor }}
                  _hover={{ borderColor: buttonHoverBorderColor }}
                  _focus={{
                    borderColor: 'blue.500',
                    boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                  }}
                />
                {versionErrors.latestversion && (
                  <Text fontSize="xs" color="red.500" mt={1}>{versionErrors.latestversion}</Text>
                )}
              </FormControl>

              <FormControl isRequired isInvalid={!!versionErrors.minimalSupportedVersion}>
                <FormLabel color={headingColor} fontSize="sm" fontWeight="medium" >Minimum Supported Version</FormLabel>
                <Input
                  placeholder="Enter Minimum Supported application Version like 1.0 / 2.0"
                  value={newApplication.minimalSupportedVersion}
                  onChange={(e) => {
                    setNewApplication({ ...newApplication, minimalSupportedVersion: e.target.value });
                    // Clear errors when user starts typing
                    if (versionErrors.minimalSupportedVersion) {
                      setVersionErrors(prev => ({ ...prev, minimalSupportedVersion: '' }));
                    }
                  }}
                  bg={inputBg}
                  borderColor={borderColor}
                  color={headingColor}
                  _placeholder={{ color: mutedTextColor }}
                  _hover={{ borderColor: buttonHoverBorderColor }}
                  _focus={{
                    borderColor: 'blue.500',
                    boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                  }}
                />
                {versionErrors.minimalSupportedVersion && (
                  <Text fontSize="xs" color="red.500" mt={1}>{versionErrors.minimalSupportedVersion}</Text>
                )}
              </FormControl>
              <FormControl>
                <FormLabel color={headingColor} fontSize="sm" fontWeight="medium">Description</FormLabel>
                <Textarea
                  placeholder="Enter application description"
                  value={newApplication.description}
                  onChange={(e) => setNewApplication({ ...newApplication, description: e.target.value })}
                  rows={3}
                  bg={inputBg}
                  borderColor={borderColor}
                  color={headingColor}
                  _placeholder={{ color: mutedTextColor }}
                  _hover={{ borderColor: buttonHoverBorderColor }}
                  _focus={{
                    borderColor: 'blue.500',
                    boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                  }}
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter borderTop="1px" borderTopColor={borderColor}>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={handleCreateApplication}
              _hover={{ bg: blueHoverColor }}
            >
              {isEditMode ? 'Update Application' : 'Create Application'}
            </Button>
            <Button
              variant="ghost"
              onClick={handleModalClose}
              color={textColor}
              _hover={{ bg: hoverBg, color: headingColor }}
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>


    </Box>
  );
};

export default ApplicationsManagement;